"use client";

import { Autocomplete } from "@/components/ui/autocomplete";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { authenticatedFetch } from "@/lib/auth";
import type { PaginatedResults } from "@/lib/crunchbase";
import type { CompanyRow } from "@/lib/crunchbase";
import { CATEGORIES_TAXONOMY } from "@/lib/crunchbase/types";
import { cn } from "@/lib/utils";
import {
  ArrowUpDown,
  Award,
  Briefcase,
  Building,
  Calendar,
  Check,
  ChevronDown,
  ChevronRight,
  Copy,
  DollarSign,
  ExternalLink,
  Globe,
  Hash,
  Mail,
  MapPin,
  Search,
  Tag,
  TrendingUp,
  Users,
  X,
  Zap,
} from "lucide-react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useEffect, useState } from "react";
import React from "react";

interface CompanyExplorerProps {
  initialCompanies?: CompanyRow[];
  initialPagination?: {
    total: number;
    limit: number;
    offset: number;
    totalPages: number;
  };
}

// Helper function to create query strings
function createQueryString(params: Record<string, string>) {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value) {
      searchParams.append(key, value);
    }
  });
  return searchParams.toString();
}

// Column names and titles for the table
const COLUMNS = [
  { key: "name", title: "Company" },
  {
    key: "description",
    title: "Description",
    className: "hidden lg:table-cell",
  },
  { key: "industry", title: "Industry", className: "hidden md:table-cell" },
  { key: "location", title: "Location", className: "hidden md:table-cell" },
  { key: "linkedin_url", title: "LinkedIn", className: "hidden md:table-cell" },
  {
    key: "twitter_handle",
    title: "X (Twitter)",
    className: "hidden md:table-cell",
  },
  { key: "facebook_url", title: "Facebook", className: "hidden md:table-cell" },
  {
    key: "instagram_handle",
    title: "Instagram",
    className: "hidden md:table-cell",
  },
  { key: "contact_email", title: "Email", className: "hidden md:table-cell" },
  { key: "phone_number", title: "Phone", className: "hidden md:table-cell" },
  { key: "founders", title: "Founders", className: "hidden lg:table-cell" },
  { key: "company_type", title: "Type", className: "hidden xl:table-cell" },
  {
    key: "operating_status",
    title: "Status",
    className: "hidden xl:table-cell",
  },
  {
    key: "semrush_global_rank",
    title: "Rank",
    sortable: true,
    className: "hidden",
  },
  { key: "founded_year", title: "Founded", sortable: true },
  { key: "size", title: "Size", className: "text-right" },
  {
    key: "funding_total",
    title: "Funding",
    className: "hidden xl:table-cell text-right",
  },
  {
    key: "revenue",
    title: "Revenue",
    className: "hidden text-right",
  },
];

// Create years array for founding year dropdown
const FOUNDING_YEARS = Array.from({ length: 2024 - 1980 }, (_, i) => 2024 - i);

// Add a helper function to extract Twitter handle from URLs
const extractTwitterHandle = (twitterUrl: string | null): string | null => {
  if (!twitterUrl) return null;

  // Try to extract from URL
  try {
    // Handle different URL formats
    if (twitterUrl.includes("twitter.com/") || twitterUrl.includes("x.com/")) {
      // If it's a URL, extract the handle
      const url = new URL(
        twitterUrl.startsWith("http") ? twitterUrl : `https://${twitterUrl}`,
      );
      const pathParts = url.pathname.split("/").filter(Boolean);
      if (pathParts.length > 0) {
        return pathParts[0];
      }
    } else if (twitterUrl.startsWith("@")) {
      // If it's already a handle with @
      return twitterUrl.substring(1);
    } else {
      // If it's just the handle without @
      return twitterUrl;
    }
  } catch (e) {
    // If it's not a valid URL, just return as is
    return twitterUrl;
  }

  return null;
};

// Get dynamic logo URL for a company
const getCompanyLogoUrl = (company: CompanyRow): string => {
  if (company.logo_url) {
    return company.logo_url;
  }

  // Extract domain from website URL if available
  if (company.website) {
    try {
      const domain = company.website
        .replace(/^https?:\/\/(www\.)?/, "")
        .split("/")[0];
      return `https://img.logo.dev/${domain}`;
    } catch (e) {
      // Fallback to company name
      return `https://img.logo.dev/${encodeURIComponent(company.name || "unknown")}`;
    }
  }

  // Fallback to company name
  return company.name
    ? `https://img.logo.dev/${encodeURIComponent(company.name)}`
    : "";
};

// Add a helper function to format company type values
const formatCompanyType = (type: string | null): string => {
  if (!type) return "—";

  switch (type.toLowerCase()) {
    case "for_profit":
      return "For Profit";
    case "non_profit":
      return "Non Profit";
    default:
      return type;
  }
};

// Add a utility function to format numbers with commas
function formatNumberWithCommas(num: number): string {
  return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

export function CompanyExplorer({
  initialCompanies,
  initialPagination,
}: CompanyExplorerProps) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const [companies, setCompanies] = useState<CompanyRow[]>(
    initialCompanies || [],
  );
  const [isLoading, setIsLoading] = useState(!initialCompanies);
  const [error, setError] = useState<string | null>(null);
  const [visibleColumns, setVisibleColumns] = useState<string[]>([
    "name",
    "description",
    "linkedin_url",
    "twitter_handle",
    "facebook_url",
    "contact_email",
    "founded_year",
    "size",
    "funding_total",
  ]);

  // Search and filter states
  const [searchQuery, setSearchQuery] = useState(
    searchParams?.get("name") || "",
  );
  const [selectedCategories, setSelectedCategories] = useState<string[]>(
    searchParams?.get("categories")
      ? searchParams.get("categories")!.split(",")
      : [],
  );
  const [minSize, setMinSize] = useState(searchParams?.get("min_size") || "");
  const [maxSize, setMaxSize] = useState(searchParams?.get("max_size") || "");
  const [minFoundedYear, setMinFoundedYear] = useState(
    searchParams?.get("min_founded_year") || "",
  );
  const [maxFoundedYear, setMaxFoundedYear] = useState(
    searchParams?.get("max_founded_year") || "",
  );
  const [sortBy, setSortBy] = useState<"founded_year" | "rank" | string>(
    (searchParams?.get("order_by") as "founded_year" | "rank") || "rank",
  );
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">(
    (searchParams?.get("order_direction") as "asc" | "desc") || "asc",
  );

  // Pagination state
  const [pagination, setPagination] = useState({
    total: initialPagination?.total || 0,
    limit: initialPagination?.limit || 10,
    offset: initialPagination?.offset || 0,
    totalPages: initialPagination?.totalPages || 0,
  });

  // Track expanded rows for description display
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});

  // Track copied email states
  const [copiedEmails, setCopiedEmails] = useState<Record<string, boolean>>({});

  // Convert categories to autocomplete options
  const categoryOptions = [
    ...CATEGORIES_TAXONOMY.map((category) => ({
      value: category,
      label: category,
    })),
  ];

  // Sort options
  const sortOptions = [
    { value: "rank:asc", label: "Rank: Low to High" },
    { value: "rank:desc", label: "Rank: High to Low" },
    { value: "founded_year:desc", label: "Newest First" },
    { value: "founded_year:asc", label: "Oldest First" },
    { value: "size:desc", label: "Size: Largest First" },
    { value: "size:asc", label: "Size: Smallest First" },
  ];

  // Fetch companies based on filters
  const fetchCompanies = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const queryParams = new URLSearchParams();

      if (searchQuery) queryParams.append("name", searchQuery);

      // Handle multiple categories - use separate parameters for each category
      if (selectedCategories.length > 0) {
        selectedCategories.forEach((category) => {
          queryParams.append("categories", category);
        });
      }

      if (minSize) queryParams.append("min_size", minSize);
      if (maxSize) queryParams.append("max_size", maxSize);
      if (minFoundedYear && minFoundedYear !== "any")
        queryParams.append("min_founded_year", minFoundedYear);
      if (maxFoundedYear && maxFoundedYear !== "any")
        queryParams.append("max_founded_year", maxFoundedYear);
      if (sortBy) queryParams.append("order_by", sortBy);
      if (sortDirection) queryParams.append("order_direction", sortDirection);

      queryParams.append("limit", pagination.limit.toString());
      queryParams.append("offset", pagination.offset.toString());

      const response = await authenticatedFetch(
        `/api/query-companies?${queryParams.toString()}`,
      );

      if (!response.ok) {
        throw new Error("Failed to fetch companies");
      }

      const result = (await response.json()) as PaginatedResults<CompanyRow>;
      setCompanies(result.data);
      setPagination(result.pagination);
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setIsLoading(false);
    }
  };

  // Update URL with filters
  const updateUrlWithFilters = () => {
    const params: Record<string, string> = {};

    if (searchQuery) params.name = searchQuery;
    if (selectedCategories.length > 0)
      params.categories = selectedCategories.join(",");
    if (minSize) params.min_size = minSize;
    if (maxSize) params.max_size = maxSize;
    if (minFoundedYear && minFoundedYear !== "any")
      params.min_founded_year = minFoundedYear;
    if (maxFoundedYear && maxFoundedYear !== "any")
      params.max_founded_year = maxFoundedYear;
    if (sortBy) params.order_by = sortBy;
    if (sortDirection) params.order_direction = sortDirection;
    if (pagination.offset > 0) params.offset = pagination.offset.toString();

    const queryString = createQueryString(params);
    router.push(`${pathname}?${queryString}`);
  };

  // Handle search
  const handleSearch = () => {
    setPagination((prev) => ({ ...prev, offset: 0 }));
    updateUrlWithFilters();
    fetchCompanies();
  };

  // Handle sort
  const handleSort = (field: string) => {
    if (sortBy === field) {
      // Toggle direction if same field
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      // Set new field with default direction
      setSortBy(field);
      setSortDirection("asc");
    }
  };

  // Handle sorting option change
  const handleSortChange = (value: string) => {
    const [field, direction] = value.split(":");
    setSortBy(field);
    setSortDirection(direction as "asc" | "desc");
  };

  // Handle pagination
  const handleNextPage = () => {
    const newOffset = pagination.offset + pagination.limit;
    setPagination((prev) => ({ ...prev, offset: newOffset }));
  };

  const handlePrevPage = () => {
    const newOffset = Math.max(pagination.offset - pagination.limit, 0);
    setPagination((prev) => ({ ...prev, offset: newOffset }));
  };

  // Reset all filters
  const resetFilters = () => {
    setSearchQuery("");
    setSelectedCategories([]);
    setMinSize("");
    setMaxSize("");
    setMinFoundedYear("");
    setMaxFoundedYear("");
    setSortBy("rank");
    setSortDirection("asc");
    setPagination((prev) => ({ ...prev, offset: 0 }));
    router.push(pathname);
    fetchCompanies();
  };

  // Initialize and update when filters change
  useEffect(() => {
    fetchCompanies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.offset, sortBy, sortDirection]);

  useEffect(() => {
    updateUrlWithFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    searchQuery,
    selectedCategories,
    minSize,
    maxSize,
    minFoundedYear,
    maxFoundedYear,
    sortBy,
    sortDirection,
    pagination.offset,
  ]);

  // Format employee size for display
  const formatEmployeeSize = (size: number | null) => {
    if (size === null) return "Unknown";
    if (size < 10) return "1-10";
    if (size < 50) return "11-50";
    if (size < 200) return "51-200";
    if (size < 500) return "201-500";
    if (size < 1000) return "501-1000";
    if (size < 5000) return "1001-5000";
    return "5000+";
  };

  // Format currency for display
  const formatCurrency = (value: number | null) => {
    if (value === null) return "—";
    if (value >= 1_000_000_000)
      return `$${(value / 1_000_000_000).toFixed(1)}B`;
    if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
    if (value >= 1_000) return `$${(value / 1_000).toFixed(0)}K`;
    return `$${value.toLocaleString()}`;
  };

  // Toggle column visibility
  const toggleColumn = (columnKey: string) => {
    setVisibleColumns((prev) =>
      prev.includes(columnKey)
        ? prev.filter((key) => key !== columnKey)
        : [...prev, columnKey],
    );
  };

  // Get active filter count
  const getActiveFilterCount = () => {
    let count = 0;
    if (searchQuery) count++;
    if (selectedCategories.length > 0) count++;
    if (minSize || maxSize) count++;
    if (minFoundedYear && minFoundedYear !== "any") count++;
    if (maxFoundedYear && maxFoundedYear !== "any") count++;
    if (sortBy && sortBy !== "rank") count++;
    return count;
  };

  // Toggle row expansion
  const toggleRowExpansion = (id: string) => {
    setExpandedRows((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  // Copy email to clipboard
  const copyEmailToClipboard = async (email: string, id: string) => {
    try {
      await navigator.clipboard.writeText(email);
      setCopiedEmails((prev) => ({ ...prev, [id]: true }));

      // Reset the copied state after 2 seconds
      setTimeout(() => {
        setCopiedEmails((prev) => ({ ...prev, [id]: false }));
      }, 2000);
    } catch (error) {
      console.error("Failed to copy email:", error);
    }
  };

  return (
    <div className="space-y-6">
      {/* Search and filter bar */}
      <div className="bg-white rounded-lg border shadow-sm">
        <div className="p-4">
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Search input */}
            <div className="flex-1">
              <div className="relative">
                <Input
                  placeholder="Search companies..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                  onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                />
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              </div>
            </div>

            {/* Category filter */}
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="justify-between gap-2 w-full lg:w-auto min-w-[160px]"
                >
                  <span className="flex items-center gap-2">
                    <Building className="h-4 w-4" />
                    Categories
                  </span>
                  {selectedCategories.length > 0 && (
                    <Badge variant="secondary" className="ml-2">
                      {selectedCategories.length}
                    </Badge>
                  )}
                  <ChevronDown className="h-3.5 w-3.5 ml-2" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-80 p-4" align="end">
                <div className="space-y-4">
                  <h4 className="font-semibold">Categories</h4>
                  <div className="space-y-4">
                    <Autocomplete
                      options={categoryOptions}
                      value={
                        selectedCategories.length > 0
                          ? selectedCategories[selectedCategories.length - 1]
                          : ""
                      }
                      onChange={(value) => {
                        if (value && !selectedCategories.includes(value)) {
                          setSelectedCategories([...selectedCategories, value]);
                        }
                      }}
                      placeholder="Select a category"
                      emptyMessage="No categories found"
                      searchPlaceholder="Search categories..."
                    />
                    <div className="flex flex-wrap gap-2">
                      {selectedCategories.map((category) => (
                        <Badge
                          key={category}
                          variant="outline"
                          className="flex items-center gap-1 px-3 py-1.5"
                        >
                          {category}
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-auto p-0.5 hover:bg-transparent ml-1"
                            onClick={() =>
                              setSelectedCategories(
                                selectedCategories.filter(
                                  (c) => c !== category,
                                ),
                              )
                            }
                          >
                            <X className="h-3 w-3" />
                          </Button>
                        </Badge>
                      ))}
                    </div>
                  </div>
                </div>
              </PopoverContent>
            </Popover>

            {/* Company size filter */}
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="justify-between gap-2 w-full lg:w-auto"
                >
                  <span className="flex items-center gap-2">
                    <Users className="h-4 w-4" />
                    Company Size
                  </span>
                  {(minSize || maxSize) && (
                    <Badge variant="secondary" className="ml-2">
                      1
                    </Badge>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-80 p-4" align="end">
                <div className="space-y-4">
                  <h4 className="font-semibold">Company Size</h4>
                  <div className="flex items-center gap-2">
                    <Input
                      placeholder="Min"
                      value={minSize}
                      onChange={(e) => setMinSize(e.target.value)}
                      type="number"
                      className="w-full"
                    />
                    <span>-</span>
                    <Input
                      placeholder="Max"
                      value={maxSize}
                      onChange={(e) => setMaxSize(e.target.value)}
                      type="number"
                      className="w-full"
                    />
                  </div>
                </div>
              </PopoverContent>
            </Popover>

            {/* Founded year filter */}
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="justify-between gap-2 w-full lg:w-auto"
                >
                  <span className="flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    Founded Year
                  </span>
                  {(minFoundedYear && minFoundedYear !== "any") ||
                  (maxFoundedYear && maxFoundedYear !== "any") ? (
                    <Badge variant="secondary" className="ml-2">
                      1
                    </Badge>
                  ) : null}
                  <ChevronDown className="h-3.5 w-3.5 ml-2" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-80 p-4" align="end">
                <div className="space-y-4">
                  <h4 className="font-semibold">Founded Year</h4>
                  <div className="flex items-center gap-2">
                    <div className="flex-1">
                      <p className="text-sm text-muted-foreground mb-2">From</p>
                      <Select
                        value={minFoundedYear}
                        onValueChange={setMinFoundedYear}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Any year" />
                        </SelectTrigger>
                        <SelectContent className="max-h-60">
                          <SelectItem value="any">Any year</SelectItem>
                          {FOUNDING_YEARS.map((year) => (
                            <SelectItem
                              key={`min-${year}`}
                              value={year.toString()}
                            >
                              {year}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <span>-</span>
                    <div className="flex-1">
                      <p className="text-sm text-muted-foreground mb-2">To</p>
                      <Select
                        value={maxFoundedYear}
                        onValueChange={setMaxFoundedYear}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Any year" />
                        </SelectTrigger>
                        <SelectContent className="max-h-60">
                          <SelectItem value="any">Any year</SelectItem>
                          {FOUNDING_YEARS.map((year) => (
                            <SelectItem
                              key={`max-${year}`}
                              value={year.toString()}
                            >
                              {year}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              </PopoverContent>
            </Popover>

            {/* Sort filter */}
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="justify-between gap-2 w-full lg:w-auto"
                >
                  <span className="flex items-center gap-2">
                    <Zap className="h-4 w-4" />
                    {sortOptions.find(
                      (o) => o.value === `${sortBy}:${sortDirection}`,
                    )?.label || "Sort"}
                  </span>
                  <ChevronDown className="h-3.5 w-3.5 ml-2" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-80 p-4" align="end">
                <div className="space-y-4">
                  <h4 className="font-semibold">Sort By</h4>
                  <Autocomplete
                    options={sortOptions}
                    value={`${sortBy}:${sortDirection}`}
                    onChange={handleSortChange}
                    placeholder="Select sorting option"
                  />
                </div>
              </PopoverContent>
            </Popover>

            {/* Apply filters button */}
            <Button
              onClick={handleSearch}
              className="w-full lg:w-auto"
              disabled={isLoading}
            >
              Apply Filters
            </Button>
          </div>
        </div>

        {/* Secondary filter options */}
        <div className="border-t px-4 py-3 flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="gap-2 h-8">
                  <ChevronDown className="h-3.5 w-3.5" />
                  Columns
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-48">
                {COLUMNS.map((column) => (
                  <DropdownMenuCheckboxItem
                    key={column.key}
                    checked={visibleColumns.includes(column.key)}
                    onCheckedChange={() => toggleColumn(column.key)}
                  >
                    {column.title}
                  </DropdownMenuCheckboxItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {getActiveFilterCount() > 0 && (
              <div className="text-sm text-muted-foreground">
                {getActiveFilterCount()} filter
                {getActiveFilterCount() !== 1 ? "s" : ""} applied
              </div>
            )}
          </div>

          <Button
            variant="ghost"
            size="sm"
            onClick={resetFilters}
            className="h-8 text-sm"
          >
            Reset Filters
          </Button>
        </div>
      </div>

      {error ? (
        <Card className="border-destructive bg-destructive/10">
          <CardHeader>
            <CardTitle>Something went wrong</CardTitle>
          </CardHeader>
          <CardContent>
            <p>{error}</p>
          </CardContent>
          <CardFooter>
            <Button variant="outline" onClick={fetchCompanies}>
              Try Again
            </Button>
          </CardFooter>
        </Card>
      ) : (
        <>
          <div className="bg-white rounded-lg border shadow-sm overflow-hidden">
            <Table>
              <TableHeader className="bg-muted/50">
                <TableRow className="hover:bg-transparent">
                  <TableHead className="w-0" />
                  {COLUMNS.filter((col) =>
                    visibleColumns.includes(col.key),
                  ).map((column) => (
                    <TableHead key={column.key} className={column.className}>
                      {column.sortable ? (
                        <Button
                          variant="ghost"
                          onClick={() => handleSort(column.key)}
                          className="flex items-center gap-1 h-8 px-2 -ml-2 font-semibold"
                        >
                          {column.title}
                          {sortBy === column.key && (
                            <ArrowUpDown
                              className={cn(
                                "h-4 w-4",
                                sortDirection === "desc" &&
                                  "rotate-180 transform",
                              )}
                            />
                          )}
                        </Button>
                      ) : (
                        column.title
                      )}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  // Skeleton loader
                  ["sk1", "sk2", "sk3", "sk4", "sk5"].map((skId) => (
                    <TableRow key={skId} className="hover:bg-transparent">
                      {COLUMNS.filter((col) =>
                        visibleColumns.includes(col.key),
                      ).map((column) => (
                        <TableCell
                          key={`${skId}-${column.key}`}
                          className={column.className}
                        >
                          {column.key === "name" ? (
                            <div className="flex items-center gap-3">
                              <Skeleton className="h-8 w-8 rounded-full" />
                              <div className="space-y-1.5">
                                <Skeleton className="h-4 w-24" />
                                <Skeleton className="h-3 w-16" />
                              </div>
                            </div>
                          ) : (
                            <Skeleton
                              className={cn(
                                "h-4",
                                column.key === "description"
                                  ? "w-full max-w-xs"
                                  : "w-16",
                              )}
                            />
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : companies.length === 0 ? (
                  <TableRow className="hover:bg-transparent">
                    <TableCell
                      colSpan={visibleColumns.length}
                      className="text-center py-8"
                    >
                      <div className="space-y-2">
                        <p className="text-muted-foreground">
                          No companies found
                        </p>
                        <Button variant="outline" onClick={resetFilters}>
                          Reset Filters
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  companies.map((company) => (
                    <React.Fragment key={company.id}>
                      <TableRow
                        key={`row-${company.id}`}
                        className="border-b last:border-b-0 cursor-pointer"
                        onClick={() => toggleRowExpansion(company.id)}
                      >
                        <TableCell className="w-0 p-0 pl-4">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleRowExpansion(company.id);
                            }}
                          >
                            <ChevronRight
                              className={cn(
                                "h-4 w-4 transition-transform",
                                expandedRows[company.id] && "rotate-90",
                              )}
                            />
                          </Button>
                        </TableCell>

                        {visibleColumns.includes("name") && (
                          <TableCell>
                            <div className="flex items-center gap-3">
                              {company.logo_url ||
                              company.website ||
                              company.name ? (
                                <div className="h-8 w-8 overflow-hidden flex-shrink-0">
                                  <img
                                    src={getCompanyLogoUrl(company)}
                                    alt={`${company.name || "Unknown"} logo`}
                                    className="h-full w-full object-cover rounded-full"
                                    onError={(e) => {
                                      // Fallback to initials if image fails to load
                                      e.currentTarget.style.display = "none";
                                      e.currentTarget.parentElement!.innerHTML =
                                        `<div class="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center"><span class="text-xs font-semibold text-primary">${company.name?.[0]?.toUpperCase() || "?"}</span></div>`;
                                    }}
                                  />
                                </div>
                              ) : (
                                <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                                  <span className="text-xs font-semibold text-primary">
                                    {company.name?.[0]?.toUpperCase() || "?"}
                                  </span>
                                </div>
                              )}
                              <div>
                                <div className="font-semibold">
                                  {company.name || "Unknown"}
                                </div>
                                {company.website && (
                                  <a
                                    href={company.website}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-xs text-muted-foreground flex items-center gap-1 hover:text-primary"
                                  >
                                    {
                                      company.website
                                        .replace(/^https?:\/\/(www\.)?/, "")
                                        .split("/")[0]
                                    }
                                    <ExternalLink className="h-3 w-3" />
                                  </a>
                                )}
                              </div>
                            </div>
                          </TableCell>
                        )}

                        {visibleColumns.includes("description") && (
                          <TableCell className="hidden lg:table-cell max-w-xs">
                            <p className="line-clamp-2">
                              {company.description || "—"}
                            </p>
                          </TableCell>
                        )}

                        {visibleColumns.includes("linkedin_url") && (
                          <TableCell className="hidden md:table-cell">
                            {company.linkedin_url ? (
                              <a
                                href={company.linkedin_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-sm flex items-center gap-1 text-blue-600 hover:underline"
                              >
                                <svg
                                  className="h-4 w-4"
                                  viewBox="0 0 24 24"
                                  fill="currentColor"
                                >
                                  <path d="M19 3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14m-.5 15.5v-5.3a3.26 3.26 0 0 0-3.26-3.26c-.85 0-1.84.52-2.32 1.3v-1.11h-2.79v8.37h2.79v-4.93c0-.77.62-1.4 1.39-1.4a1.4 1.4 0 0 1 1.4 1.4v4.93h2.79M6.88 8.56a1.68 1.68 0 0 0 1.68-1.68c0-.93-.75-1.69-1.68-1.69a1.69 1.69 0 0 0-1.69 1.69c0 .93.76 1.68 1.69 1.68m1.39 9.94v-8.37H5.5v8.37h2.77z" />
                                </svg>
                                <span className="hidden xl:inline">
                                  LinkedIn
                                </span>
                              </a>
                            ) : (
                              "—"
                            )}
                          </TableCell>
                        )}

                        {visibleColumns.includes("twitter_handle") && (
                          <TableCell className="hidden md:table-cell">
                            {company.twitter_handle ? (
                              <a
                                href={`https://twitter.com/${extractTwitterHandle(company.twitter_handle)}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-sm flex items-center gap-1 text-black hover:underline"
                              >
                                <svg
                                  className="h-4 w-4"
                                  viewBox="0 0 24 24"
                                  fill="currentColor"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                                </svg>
                                <span className="hidden xl:inline">
                                  @
                                  {extractTwitterHandle(company.twitter_handle)}
                                </span>
                                <span className="xl:hidden">
                                  @
                                  {extractTwitterHandle(company.twitter_handle)}
                                </span>
                              </a>
                            ) : (
                              "—"
                            )}
                          </TableCell>
                        )}

                        {visibleColumns.includes("facebook_url") && (
                          <TableCell className="hidden md:table-cell">
                            {company.facebook_url ? (
                              <a
                                href={company.facebook_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-sm flex items-center gap-1 text-blue-600 hover:underline"
                              >
                                <svg
                                  className="h-4 w-4"
                                  viewBox="0 0 24 24"
                                  fill="currentColor"
                                >
                                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                                </svg>
                                <span className="hidden xl:inline">
                                  Facebook
                                </span>
                              </a>
                            ) : (
                              "—"
                            )}
                          </TableCell>
                        )}

                        {visibleColumns.includes("instagram_handle") && (
                          <TableCell className="hidden md:table-cell">
                            {company.instagram_handle ? (
                              <a
                                href={`https://instagram.com/${company.instagram_handle.replace("@", "")}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-sm flex items-center gap-1 text-pink-600 hover:underline"
                              >
                                <svg
                                  className="h-4 w-4"
                                  viewBox="0 0 24 24"
                                  fill="currentColor"
                                >
                                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                                </svg>
                                <span className="hidden xl:inline">
                                  @{company.instagram_handle.replace("@", "")}
                                </span>
                              </a>
                            ) : (
                              "—"
                            )}
                          </TableCell>
                        )}

                        {visibleColumns.includes("contact_email") && (
                          <TableCell className="hidden md:table-cell">
                            {company.contact_email ? (
                              <div className="flex items-center gap-1">
                                <a
                                  href={`mailto:${company.contact_email}`}
                                  className="text-sm flex items-center gap-1 text-blue-600 hover:underline"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <svg
                                    className="h-4 w-4"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                  >
                                    <path d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                  </svg>
                                  <span className="truncate max-w-[120px]">
                                    {company.contact_email}
                                  </span>
                                </a>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-6 w-6 p-0 ml-1"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    copyEmailToClipboard(
                                      company.contact_email!,
                                      company.id,
                                    );
                                  }}
                                >
                                  {copiedEmails[company.id] ? (
                                    <Check className="h-3.5 w-3.5 text-green-500" />
                                  ) : (
                                    <Copy className="h-3.5 w-3.5 text-muted-foreground" />
                                  )}
                                </Button>
                              </div>
                            ) : (
                              "—"
                            )}
                          </TableCell>
                        )}

                        {visibleColumns.includes("phone_number") && (
                          <TableCell className="hidden md:table-cell">
                            {company.phone_number ? (
                              <a
                                href={`tel:${company.phone_number}`}
                                className="text-sm flex items-center gap-1 text-blue-600 hover:underline"
                              >
                                <svg
                                  className="h-4 w-4"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                >
                                  <path d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                                </svg>
                                {company.phone_number}
                              </a>
                            ) : (
                              "—"
                            )}
                          </TableCell>
                        )}

                        {visibleColumns.includes("founders") && (
                          <TableCell className="hidden lg:table-cell">
                            {company.founders || "—"}
                          </TableCell>
                        )}

                        {visibleColumns.includes("founded_year") && (
                          <TableCell>{company.founded_year || "—"}</TableCell>
                        )}

                        {visibleColumns.includes("size") && (
                          <TableCell className="text-right">
                            {formatEmployeeSize(company.size)}
                          </TableCell>
                        )}

                        {visibleColumns.includes("funding_total") && (
                          <TableCell className="hidden xl:table-cell text-right">
                            {formatCurrency(company.funding_total)}
                          </TableCell>
                        )}

                        {visibleColumns.includes("revenue") && (
                          <TableCell className="hidden text-right">
                            {formatCurrency(company.revenue)}
                          </TableCell>
                        )}
                      </TableRow>

                      {/* Expanded description row */}
                      {expandedRows[company.id] && (
                        <TableRow className="bg-gradient-to-br from-white to-slate-50">
                          <TableCell
                            colSpan={visibleColumns.length + 1}
                            className="p-6"
                          >
                            <div className="space-y-6">
                              {/* Company Header */}
                              <div className="flex items-start gap-4">
                                <div className="h-16 w-16 overflow-hidden flex-shrink-0">
                                  <img
                                    src={getCompanyLogoUrl(company)}
                                    alt={`${company.name || "Unknown"} logo`}
                                    className="h-full w-full object-cover rounded-md"
                                    onError={(e) => {
                                      e.currentTarget.style.display = "none";
                                      e.currentTarget.parentElement!.innerHTML =
                                        `<div class="h-16 w-16 rounded-md bg-primary/10 flex items-center justify-center"><span class="text-xl font-semibold text-primary">${company.name?.[0]?.toUpperCase() || "?"}</span></div>`;
                                    }}
                                  />
                                </div>
                                <div className="flex-1">
                                  <h3 className="text-xl font-semibold mb-1 flex items-center gap-2">
                                    {company.name || "Unknown"}
                                    {company.is_verified && (
                                      <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                                        Verified
                                      </span>
                                    )}
                                  </h3>
                                  <div className="flex items-center gap-2 flex-wrap text-sm text-muted-foreground">
                                    {company.website && (
                                      <a
                                        href={company.website}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center gap-1 hover:text-primary"
                                      >
                                        <Globe className="h-3.5 w-3.5" />
                                        <span>
                                          {
                                            company.website
                                              .replace(
                                                /^https?:\/\/(www\.)?/,
                                                "",
                                              )
                                              .split("/")[0]
                                          }
                                        </span>
                                      </a>
                                    )}
                                    {company.industry && (
                                      <span className="flex items-center gap-1">
                                        <Briefcase className="h-3.5 w-3.5" />
                                        <span>{company.industry}</span>
                                      </span>
                                    )}
                                    {company.founded_year && (
                                      <span className="flex items-center gap-1">
                                        <Calendar className="h-3.5 w-3.5" />
                                        <span>
                                          Founded {company.founded_year}
                                        </span>
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>

                              {/* Main Grid Layout */}
                              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                                {/* Left Column - Description and Basic Info */}
                                <div className="lg:col-span-2 space-y-5">
                                  {/* Description Card */}
                                  {company.description && (
                                    <div className="bg-white rounded-lg border p-4">
                                      <h4 className="text-sm font-semibold mb-3 text-muted-foreground flex items-center gap-1">
                                        <Tag className="h-4 w-4" />
                                        <span>COMPANY DESCRIPTION</span>
                                      </h4>
                                      <p className="text-sm leading-relaxed">
                                        {company.description}
                                      </p>
                                    </div>
                                  )}

                                  {/* Location Card */}
                                  {company.location && (
                                    <div className="bg-white rounded-lg border p-4">
                                      <h4 className="text-sm font-semibold mb-3 text-muted-foreground flex items-center gap-1">
                                        <MapPin className="h-4 w-4" />
                                        <span>LOCATION</span>
                                      </h4>
                                      <p className="text-sm">
                                        {company.location}
                                      </p>
                                    </div>
                                  )}

                                  {/* Categories */}
                                  {company.categories &&
                                    company.categories.length > 0 && (
                                      <div className="bg-white rounded-lg border p-4">
                                        <h4 className="text-sm font-semibold mb-3 text-muted-foreground flex items-center gap-1">
                                          <Hash className="h-4 w-4" />
                                          <span>CATEGORIES</span>
                                        </h4>
                                        <div className="flex flex-wrap gap-2">
                                          {company.categories.map(
                                            (category) => (
                                              <span
                                                key={category}
                                                className="bg-blue-50 text-blue-700 text-xs px-2 py-1 rounded-full"
                                              >
                                                {category}
                                              </span>
                                            ),
                                          )}
                                        </div>
                                      </div>
                                    )}

                                  {/* Founders */}
                                  {company.founders && (
                                    <div className="bg-white rounded-lg border p-4">
                                      <h4 className="text-sm font-semibold mb-3 text-muted-foreground flex items-center gap-1">
                                        <Award className="h-4 w-4" />
                                        <span>FOUNDERS</span>
                                      </h4>
                                      <p className="text-sm">
                                        {company.founders}
                                      </p>
                                    </div>
                                  )}
                                </div>

                                {/* Right Column - Stats, Contact, Social */}
                                <div className="space-y-5">
                                  {/* Company Stats Card */}
                                  <div className="bg-white rounded-lg border p-4">
                                    <h4 className="text-sm font-semibold mb-3 text-muted-foreground flex items-center gap-1">
                                      <TrendingUp className="h-4 w-4" />
                                      <span>COMPANY STATS</span>
                                    </h4>
                                    <div className="space-y-3">
                                      {company.semrush_global_rank && (
                                        <div className="flex justify-between items-center">
                                          <span className="text-sm text-muted-foreground">
                                            Global Rank
                                          </span>
                                          <span className="font-semibold text-sm">
                                            #
                                            {company.semrush_global_rank.toLocaleString()}
                                          </span>
                                        </div>
                                      )}
                                      {company.size !== null && (
                                        <div className="flex justify-between items-center">
                                          <span className="text-sm text-muted-foreground">
                                            Company Size
                                          </span>
                                          <span className="font-semibold text-sm">
                                            {formatEmployeeSize(company.size)}
                                          </span>
                                        </div>
                                      )}
                                      {company.company_type && (
                                        <div className="flex justify-between items-center">
                                          <span className="text-sm text-muted-foreground">
                                            Company Type
                                          </span>
                                          <span className="font-semibold text-sm">
                                            {formatCompanyType(
                                              company.company_type,
                                            )}
                                          </span>
                                        </div>
                                      )}
                                      {company.operating_status && (
                                        <div className="flex justify-between items-center">
                                          <span className="text-sm text-muted-foreground">
                                            Status
                                          </span>
                                          <span className="font-semibold text-sm">
                                            {company.operating_status}
                                          </span>
                                        </div>
                                      )}
                                    </div>
                                  </div>

                                  {/* Financial Info Card */}
                                  {(company.funding_total ||
                                    company.revenue ||
                                    company.last_funding_type) && (
                                    <div className="bg-white rounded-lg border p-4">
                                      <h4 className="text-sm font-semibold mb-3 text-muted-foreground flex items-center gap-1">
                                        <DollarSign className="h-4 w-4" />
                                        <span>FINANCIAL DATA</span>
                                      </h4>
                                      <div className="space-y-3">
                                        {company.funding_total && (
                                          <div className="flex justify-between items-center">
                                            <span className="text-sm text-muted-foreground">
                                              Total Funding
                                            </span>
                                            <span className="font-semibold text-sm">
                                              {formatCurrency(
                                                company.funding_total,
                                              )}
                                            </span>
                                          </div>
                                        )}
                                        {company.revenue && (
                                          <div className="flex justify-between items-center">
                                            <span className="text-sm text-muted-foreground">
                                              Revenue
                                            </span>
                                            <span className="font-semibold text-sm">
                                              {formatCurrency(company.revenue)}
                                            </span>
                                          </div>
                                        )}
                                        {company.last_funding_type && (
                                          <div className="flex justify-between items-center">
                                            <span className="text-sm text-muted-foreground">
                                              Last Funding
                                            </span>
                                            <span className="font-semibold text-sm">
                                              {company.last_funding_type}
                                            </span>
                                          </div>
                                        )}
                                        {company.num_funding_rounds && (
                                          <div className="flex justify-between items-center">
                                            <span className="text-sm text-muted-foreground">
                                              Funding Rounds
                                            </span>
                                            <span className="font-semibold text-sm">
                                              {company.num_funding_rounds}
                                            </span>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  )}

                                  {/* Contact Info Card */}
                                  {(company.contact_email ||
                                    company.phone_number) && (
                                    <div className="bg-white rounded-lg border p-4">
                                      <h4 className="text-sm font-semibold mb-3 text-muted-foreground flex items-center gap-1">
                                        <Mail className="h-4 w-4" />
                                        <span>CONTACT</span>
                                      </h4>
                                      <div className="space-y-3">
                                        {company.contact_email && (
                                          <div className="flex items-center justify-between">
                                            <span className="text-sm text-muted-foreground">
                                              Email
                                            </span>
                                            <div className="flex items-center gap-1">
                                              <a
                                                href={`mailto:${company.contact_email}`}
                                                className="text-sm text-blue-600 hover:underline"
                                                onClick={(e) =>
                                                  e.stopPropagation()
                                                }
                                              >
                                                {company.contact_email}
                                              </a>
                                              <Button
                                                variant="ghost"
                                                size="sm"
                                                className="h-6 w-6 p-0"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  copyEmailToClipboard(
                                                    company.contact_email!,
                                                    company.id,
                                                  );
                                                }}
                                              >
                                                {copiedEmails[company.id] ? (
                                                  <Check className="h-3.5 w-3.5 text-green-500" />
                                                ) : (
                                                  <Copy className="h-3.5 w-3.5 text-muted-foreground" />
                                                )}
                                              </Button>
                                            </div>
                                          </div>
                                        )}
                                        {company.phone_number && (
                                          <div className="flex items-center justify-between">
                                            <span className="text-sm text-muted-foreground">
                                              Phone
                                            </span>
                                            <a
                                              href={`tel:${company.phone_number}`}
                                              className="text-sm text-blue-600 hover:underline"
                                              onClick={(e) =>
                                                e.stopPropagation()
                                              }
                                            >
                                              {company.phone_number}
                                            </a>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  )}

                                  {/* Social Media Card */}
                                  {(company.linkedin_url ||
                                    company.twitter_handle ||
                                    company.facebook_url ||
                                    company.instagram_handle) && (
                                    <div className="bg-white rounded-lg border p-4">
                                      <h4 className="text-sm font-semibold mb-3 text-muted-foreground flex items-center gap-1">
                                        <Globe className="h-4 w-4" />
                                        <span>SOCIAL MEDIA</span>
                                      </h4>
                                      <div className="flex flex-wrap gap-2">
                                        {company.linkedin_url && (
                                          <a
                                            href={company.linkedin_url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="inline-flex items-center justify-center w-8 h-8 bg-blue-100 text-blue-600 rounded-md hover:bg-blue-200"
                                            onClick={(e) => e.stopPropagation()}
                                          >
                                            <svg
                                              className="h-4 w-4"
                                              viewBox="0 0 24 24"
                                              fill="currentColor"
                                            >
                                              <path d="M19 3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14m-.5 15.5v-5.3a3.26 3.26 0 0 0-3.26-3.26c-.85 0-1.84.52-2.32 1.3v-1.11h-2.79v8.37h2.79v-4.93c0-.77.62-1.4 1.39-1.4a1.4 1.4 0 0 1 1.4 1.4v4.93h2.79M6.88 8.56a1.68 1.68 0 0 0 1.68-1.68c0-.93-.75-1.69-1.68-1.69a1.69 1.69 0 0 0-1.69 1.69c0 .93.76 1.68 1.69 1.68m1.39 9.94v-8.37H5.5v8.37h2.77z" />
                                            </svg>
                                          </a>
                                        )}

                                        {company.twitter_handle && (
                                          <a
                                            href={`https://twitter.com/${extractTwitterHandle(company.twitter_handle)}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="inline-flex items-center justify-center w-8 h-8 bg-black text-white rounded-md hover:bg-gray-800"
                                            onClick={(e) => e.stopPropagation()}
                                          >
                                            <svg
                                              className="h-4 w-4"
                                              viewBox="0 0 24 24"
                                              fill="currentColor"
                                            >
                                              <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                                            </svg>
                                          </a>
                                        )}

                                        {company.facebook_url && (
                                          <a
                                            href={company.facebook_url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="inline-flex items-center justify-center w-8 h-8 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                                            onClick={(e) => e.stopPropagation()}
                                          >
                                            <svg
                                              className="h-4 w-4"
                                              viewBox="0 0 24 24"
                                              fill="currentColor"
                                            >
                                              <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                                            </svg>
                                          </a>
                                        )}

                                        {company.instagram_handle && (
                                          <a
                                            href={`https://instagram.com/${company.instagram_handle.replace("@", "")}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="inline-flex items-center justify-center w-8 h-8 bg-gradient-to-tr from-yellow-400 via-red-500 to-purple-500 text-white rounded-md hover:opacity-90"
                                            onClick={(e) => e.stopPropagation()}
                                          >
                                            <svg
                                              className="h-4 w-4"
                                              viewBox="0 0 24 24"
                                              fill="currentColor"
                                            >
                                              <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                                            </svg>
                                          </a>
                                        )}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </>
      )}

      {/* Pagination UI */}
      <div className="flex items-center justify-between bg-white rounded-lg border shadow-sm p-4">
        <div className="text-sm text-muted-foreground">
          {isLoading ? (
            <Skeleton className="h-4 w-40" />
          ) : (
            <>
              Showing {pagination.offset + 1}-
              {Math.min(pagination.offset + companies.length, pagination.total)}{" "}
              of {formatNumberWithCommas(pagination.total)}
            </>
          )}
        </div>

        <div className="flex items-center gap-2">
          {!isLoading && (
            <div className="text-sm text-muted-foreground mx-4">
              Page {Math.floor(pagination.offset / pagination.limit) + 1} of{" "}
              {Math.ceil(pagination.total / pagination.limit)}
            </div>
          )}
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={handlePrevPage}
              disabled={isLoading || pagination.offset === 0}
              size="sm"
            >
              Previous
            </Button>
            <Button
              variant="outline"
              onClick={handleNextPage}
              disabled={
                isLoading ||
                pagination.offset + pagination.limit >= pagination.total
              }
              size="sm"
            >
              Next
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function CompanyExplorerSkeleton() {
  // Use the same default visible columns as the main component
  const defaultVisibleColumns = [
    "name",
    "description",
    "location",
    "linkedin_url",
    "twitter_handle",
    "contact_email",
    "phone_number",
    "founded_year",
    "size",
    "funding_total",
  ];

  // Default pagination values for skeleton
  const defaultPagination = {
    total: 0,
    limit: 10,
    offset: 0,
    totalPages: 1,
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg border shadow-sm">
        <div className="p-4">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="flex-1">
              <Skeleton className="h-10 w-full" />
            </div>

            <Skeleton className="h-10 w-40" />
            <Skeleton className="h-10 w-40" />
            <Skeleton className="h-10 w-40" />
            <Skeleton className="h-10 w-40" />
            <Skeleton className="h-10 w-36" />
          </div>
        </div>

        <div className="border-t px-4 py-3 flex flex-wrap items-center justify-between gap-2">
          <Skeleton className="h-8 w-28" />
          <Skeleton className="h-8 w-28" />
        </div>
      </div>

      <div className="bg-white rounded-lg border shadow-sm overflow-hidden">
        <Table>
          <TableHeader className="bg-muted/50">
            <TableRow className="hover:bg-transparent">
              {COLUMNS.filter((col) =>
                defaultVisibleColumns.includes(col.key),
              ).map((column) => (
                <TableHead key={column.key} className={column.className}>
                  <Skeleton className="h-4 w-20" />
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {["row1", "row2", "row3", "row4", "row5"].map((rowId) => (
              <TableRow
                key={rowId}
                className="border-b last:border-b-0 hover:bg-transparent"
              >
                {COLUMNS.filter((col) =>
                  defaultVisibleColumns.includes(col.key),
                ).map((column) => (
                  <TableCell
                    key={`${rowId}-${column.key}`}
                    className={column.className}
                  >
                    {column.key === "name" ? (
                      <div className="flex items-center gap-3">
                        <Skeleton className="h-8 w-8 rounded-full" />
                        <div className="space-y-1.5">
                          <Skeleton className="h-4 w-24" />
                          <Skeleton className="h-3 w-16" />
                        </div>
                      </div>
                    ) : (
                      <Skeleton
                        className={cn(
                          "h-4",
                          column.key === "description"
                            ? "w-full max-w-xs"
                            : "w-16",
                        )}
                      />
                    )}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <div className="flex items-center justify-between bg-white rounded-lg border shadow-sm p-4">
        <Skeleton className="h-4 w-40" />
        <div className="flex items-center gap-2">
          <Skeleton className="h-4 w-24" />
          <div className="flex gap-2">
            <Skeleton className="h-8 w-24" />
            <Skeleton className="h-8 w-24" />
          </div>
        </div>
      </div>
    </div>
  );
}
